import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ClosingWorkStage,
  SmoUlClosingStatus,
  closeClosingWorkStages,
  getClosingWorkStages,
} from '../services/api';
import {
  SortState,
  compareValues,
  formatDateShort,
  toggleSort,
} from '../utils/table';
import { useAuth } from '../context/AuthContext';
import { SheetsColumnMultiFilter } from '../components/SheetsColumnMultiFilter';
import { ClosingStatusIcon, closingStatusLabel } from '../components/ClosingStatusIcon';
import { formatDeadlineCompact, formatDeadlineRelativeTo } from '../utils/deadline';
import '../styles/DataPage.css';
import './TaxesPage.css';

type ClosingStageSortKey =
  | 'contractor'
  | 'contract_number'
  | 'stage'
  | 'result'
  | 'manager'
  | 'projectLead'
  | 'start_date'
  | 'end_date'
  | 'accounting_closing_date'
  | 'overdue'
  | 'accounting_closed';

type ClosingStageFilterMenuKey =
  | 'contractor'
  | 'contract_number'
  | 'stage'
  | 'manager'
  | 'project_lead'
  | 'overdue'
  | 'accounting_closed'
  | null;

const OVERDUE_FILTER_OPTIONS = ['overdue', 'on_time', 'none'];
const CIRCLE_FILTER_OPTIONS: SmoUlClosingStatus[] = ['closed', 'open'];
const STATUS_SORT_RANK: Record<SmoUlClosingStatus, number> = {
  na: 0,
  open: 1,
  partial: 2,
  waiting: 3,
  closed: 4,
};

function contractLevel(row: ClosingWorkStage): number {
  return row.hierarchy_level ?? 1;
}

function isDeadlineExtension(row: ClosingWorkStage): boolean {
  return row.extends_stage_id != null;
}

function stageLabel(row: ClosingWorkStage): string {
  return `${row.stage_number}. ${row.stage_name}`.trim();
}

function contractDetailPath(row: ClosingWorkStage): string {
  const mainId = row.main_contract_id || row.contract_id;
  return `/contracts/${mainId}`;
}

function stageClosingStatus(row: ClosingWorkStage): SmoUlClosingStatus {
  return row.accounting_closed ? 'closed' : 'open';
}

function overdueFilterLabel(value: string): string {
  if (value === 'overdue') return 'Просрочено';
  if (value === 'on_time') return 'Не просрочено';
  return 'Нет срока';
}

function parseFilterDay(value: string): number | null {
  const v = value.trim();
  if (!v) return null;
  const ms = new Date(`${v}T00:00:00`).getTime();
  return Number.isNaN(ms) ? null : ms;
}

function dateInRange(value: string | null | undefined, from: string, to: string): boolean {
  const hasFrom = Boolean(from.trim());
  const hasTo = Boolean(to.trim());
  if (!hasFrom && !hasTo) return true;
  if (!value?.trim()) return false;

  const ts = parseFilterDay(value.slice(0, 10));
  if (ts === null) return true;

  const fromTs = hasFrom ? parseFilterDay(from) ?? Number.NEGATIVE_INFINITY : Number.NEGATIVE_INFINITY;
  const toTs = hasTo ? parseFilterDay(to) ?? Number.POSITIVE_INFINITY : Number.POSITIVE_INFINITY;
  return ts >= fromTs && ts <= toTs;
}

function todayIsoDate(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const ClosingStagesPage: React.FC = () => {
  const navigate = useNavigate();
  const { can } = useAuth();
  const [stages, setStages] = useState<ClosingWorkStage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set());
  const [dateModalOpen, setDateModalOpen] = useState(false);
  const [closingDate, setClosingDate] = useState(todayIsoDate);

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterMenuOpen, setFilterMenuOpen] = useState<ClosingStageFilterMenuKey>(null);

  const [searchText, setSearchText] = useState('');
  const [filterContractorIds, setFilterContractorIds] = useState<string[]>([]);
  const [filterContractNumbers, setFilterContractNumbers] = useState<string[]>([]);
  const [filterStages, setFilterStages] = useState<string[]>([]);
  const [filterManagerIds, setFilterManagerIds] = useState<string[]>([]);
  const [filterLeadIds, setFilterLeadIds] = useState<string[]>([]);
  const [filterStartFrom, setFilterStartFrom] = useState('');
  const [filterStartTo, setFilterStartTo] = useState('');
  const [filterEndFrom, setFilterEndFrom] = useState('');
  const [filterEndTo, setFilterEndTo] = useState('');
  const [filterOverdue, setFilterOverdue] = useState<string[]>([]);
  const [filterClosed, setFilterClosed] = useState<string[]>([]);

  const [sortState, setSortState] = useState<SortState<ClosingStageSortKey>>({
    key: 'contract_number',
    direction: 'desc',
  });

  const canOpenPage = can('CONTRACTS_CLOSING', 'CREATE');
  const canCloseFromAccounting = canOpenPage;
  const tableColSpan = (canCloseFromAccounting ? 1 : 0) + 11;

  const fetchData = useCallback(async (): Promise<void> => {
    if (!canOpenPage) {
      setIsLoading(false);
      setStages([]);
      return;
    }

    setIsLoading(true);
    setError('');
    try {
      const data = await getClosingWorkStages();
      setStages(data.stages);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка при загрузке данных');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [canOpenPage]);

  useEffect(() => {
    if (canOpenPage) {
      void fetchData();
    } else {
      setIsLoading(false);
      setStages([]);
    }
  }, [canOpenPage, fetchData]);

  const clearFilters = (): void => {
    setSearchText('');
    setFilterContractorIds([]);
    setFilterContractNumbers([]);
    setFilterStages([]);
    setFilterManagerIds([]);
    setFilterLeadIds([]);
    setFilterStartFrom('');
    setFilterStartTo('');
    setFilterEndFrom('');
    setFilterEndTo('');
    setFilterOverdue([]);
    setFilterClosed([]);
    setFilterMenuOpen(null);
  };

  const hasActiveFilters =
    filterContractorIds.length > 0 ||
    filterContractNumbers.length > 0 ||
    filterStages.length > 0 ||
    filterManagerIds.length > 0 ||
    filterLeadIds.length > 0 ||
    Boolean(filterStartFrom.trim()) ||
    Boolean(filterStartTo.trim()) ||
    Boolean(filterEndFrom.trim()) ||
    Boolean(filterEndTo.trim()) ||
    filterOverdue.length > 0 ||
    filterClosed.length > 0;

  useEffect(() => {
    if (!filtersOpen) {
      setFilterMenuOpen(null);
    }
  }, [filtersOpen]);

  const stagesByMainId = useMemo(() => {
    const map = new Map<number, ClosingWorkStage[]>();
    for (const row of stages) {
      const mainId = row.main_contract_id || row.contract_id;
      const list = map.get(mainId) ?? [];
      list.push(row);
      map.set(mainId, list);
    }
    return map;
  }, [stages]);

  const mainIds = useMemo(() => {
    const seen = new Set<number>();
    const ids: number[] = [];
    for (const row of stages) {
      const mainId = row.main_contract_id || row.contract_id;
      if (seen.has(mainId)) continue;
      seen.add(mainId);
      ids.push(mainId);
    }
    return ids;
  }, [stages]);

  const representativeByMainId = useMemo(() => {
    const map = new Map<number, ClosingWorkStage>();
    for (const row of stages) {
      const mainId = row.main_contract_id || row.contract_id;
      if (map.has(mainId)) continue;
      map.set(mainId, row);
    }
    return map;
  }, [stages]);

  const contractorFilterOptions = useMemo(() => {
    const set = new Set<string>();
    for (const row of stages) {
      if (contractLevel(row) === 1) set.add(String(row.contractor_id));
    }
    return [...set].sort((a, b) => {
      const left = stages.find((item) => String(item.contractor_id) === a)?.contractor_name ?? a;
      const right = stages.find((item) => String(item.contractor_id) === b)?.contractor_name ?? b;
      return left.localeCompare(right, 'ru');
    });
  }, [stages]);

  const numberFilterOptions = useMemo(() => {
    const set = new Set(stages.map((row) => row.contract_number));
    return [...set].sort((a, b) => a.localeCompare(b, 'ru', { numeric: true }));
  }, [stages]);

  const stageFilterOptions = useMemo(() => {
    const set = new Set(stages.map((row) => stageLabel(row)));
    return [...set].sort((a, b) => a.localeCompare(b, 'ru', { numeric: true }));
  }, [stages]);

  const managerFilterOptions = useMemo(() => {
    const set = new Set<string>();
    for (const row of stages) {
      if (contractLevel(row) === 1 && row.manager_id != null) {
        set.add(String(row.manager_id));
      }
    }
    return [...set].sort((a, b) => {
      const left = stages.find((item) => String(item.manager_id) === a)?.manager_name ?? a;
      const right = stages.find((item) => String(item.manager_id) === b)?.manager_name ?? b;
      return left.localeCompare(right, 'ru');
    });
  }, [stages]);

  const projectLeadFilterOptions = useMemo(() => {
    const set = new Set<string>();
    for (const row of stages) {
      if (contractLevel(row) === 1 && row.project_lead_id != null) {
        set.add(String(row.project_lead_id));
      }
    }
    return [...set].sort((a, b) => {
      const left = stages.find((item) => String(item.project_lead_id) === a)?.project_lead_name ?? a;
      const right = stages.find((item) => String(item.project_lead_id) === b)?.project_lead_name ?? b;
      return left.localeCompare(right, 'ru');
    });
  }, [stages]);

  const getContractorLabel = useCallback(
    (id: string): string =>
      stages.find((row) => String(row.contractor_id) === id)?.contractor_name ?? id,
    [stages],
  );

  const getManagerLabel = useCallback(
    (id: string): string =>
      stages.find((row) => String(row.manager_id) === id)?.manager_name ?? id,
    [stages],
  );

  const getLeadLabel = useCallback(
    (id: string): string =>
      stages.find((row) => String(row.project_lead_id) === id)?.project_lead_name ?? id,
    [stages],
  );

  const getDeadline = useCallback((row: ClosingWorkStage) => {
    return formatDeadlineRelativeTo(
      row.end_date,
      row.accounting_closed ? row.accounting_closing_date : null,
    );
  }, []);

  const overdueFilterValue = useCallback(
    (row: ClosingWorkStage): string => {
      const deadline = getDeadline(row);
      if (!deadline.label) return 'none';
      return deadline.overdue ? 'overdue' : 'on_time';
    },
    [getDeadline],
  );

  const searchableText = useCallback((row: ClosingWorkStage): string => {
    const deadline = getDeadline(row);
    return [
      row.contractor_name,
      row.contract_number,
      stageLabel(row),
      row.result,
      row.manager_name,
      row.project_lead_name,
      row.start_date ? formatDateShort(row.start_date) : '',
      row.end_date ? formatDateShort(row.end_date) : '',
      deadline?.label ? deadline.label.toLowerCase() : '',
      deadline ? formatDeadlineCompact(deadline) : '',
      closingStatusLabel(stageClosingStatus(row)),
      row.accounting_closing_date ? formatDateShort(row.accounting_closing_date) : '',
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
  }, [getDeadline]);

  const filteredMainIds = useMemo(() => {
    const text = searchText.trim().toLowerCase();

    return mainIds.filter((mainId) => {
      const family = stagesByMainId.get(mainId) ?? [];
      const representative = representativeByMainId.get(mainId);
      if (!representative || family.length === 0) return false;
      const matchesSearch = !text || family.some((item) => searchableText(item).includes(text));
      const matchesContractor =
        filterContractorIds.length === 0 ||
        family.some((item) => filterContractorIds.includes(String(item.contractor_id)));
      const matchesNumber =
        filterContractNumbers.length === 0 ||
        family.some((item) => filterContractNumbers.includes(item.contract_number));
      const matchesStage =
        filterStages.length === 0 || family.some((item) => filterStages.includes(stageLabel(item)));
      const matchesManager =
        filterManagerIds.length === 0 ||
        family.some((item) => filterManagerIds.includes(String(item.manager_id || '')));
      const matchesLead =
        filterLeadIds.length === 0 ||
        family.some((item) => filterLeadIds.includes(String(item.project_lead_id || '')));
      const matchesStart = family.some((item) => dateInRange(item.start_date, filterStartFrom, filterStartTo));
      const matchesEnd = family.some((item) => dateInRange(item.end_date, filterEndFrom, filterEndTo));
      const matchesOverdue =
        filterOverdue.length === 0 || family.some((item) => filterOverdue.includes(overdueFilterValue(item)));
      const matchesClosed =
        filterClosed.length === 0 ||
        family.some((item) => filterClosed.includes(stageClosingStatus(item)));

      return (
        matchesSearch &&
        matchesContractor &&
        matchesNumber &&
        matchesStage &&
        matchesManager &&
        matchesLead &&
        matchesStart &&
        matchesEnd &&
        matchesOverdue &&
        matchesClosed
      );
    });
  }, [
    mainIds,
    stagesByMainId,
    representativeByMainId,
    searchText,
    filterContractorIds,
    filterContractNumbers,
    filterStages,
    filterManagerIds,
    filterLeadIds,
    filterStartFrom,
    filterStartTo,
    filterEndFrom,
    filterEndTo,
    filterOverdue,
    filterClosed,
    searchableText,
    overdueFilterValue,
  ]);

  const sortedStages = useMemo(() => {
    const getSortValue = (
      row: ClosingWorkStage,
      key: ClosingStageSortKey,
    ): string | number | undefined => {
      const deadline = getDeadline(row);
      const values: Record<ClosingStageSortKey, string | number | undefined> = {
        contractor: row.contractor_name,
        contract_number: row.contract_number,
        stage: stageLabel(row),
        result: row.result ?? '',
        manager: row.manager_name,
        projectLead: row.project_lead_name,
        start_date: row.start_date ?? undefined,
        end_date: row.end_date ?? undefined,
        accounting_closing_date: row.accounting_closing_date ?? undefined,
        overdue: row.end_date ?? undefined,
        accounting_closed: STATUS_SORT_RANK[stageClosingStatus(row)],
      };
      if (key === 'overdue') {
        return deadline.overdue
          ? -(deadline.months ?? 0) * 30 - (deadline.days ?? 0)
          : (deadline.months ?? 0) * 30 + (deadline.days ?? 0);
      }
      return values[key];
    };

    const { key: sortKey, direction: sortDirection } = sortState;
    const sortedMains = [...filteredMainIds].sort((leftId, rightId) => {
      const left = representativeByMainId.get(leftId);
      const right = representativeByMainId.get(rightId);
      if (!left || !right) return 0;
      return compareValues(getSortValue(left, sortKey), getSortValue(right, sortKey), sortDirection);
    });

    const result: ClosingWorkStage[] = [];
    for (const mainId of sortedMains) {
      const family = stagesByMainId.get(mainId) ?? [];
      const byId = new Map(family.map((row) => [row.id, row]));
      const childrenByParent = new Map<number, ClosingWorkStage[]>();
      const topLevel: ClosingWorkStage[] = [];
      for (const row of family) {
        const parentId = row.extends_stage_id;
        const parent = parentId != null ? byId.get(parentId) : undefined;
        if (parent && parent.id !== row.id) {
          const list = childrenByParent.get(parent.id) ?? [];
          list.push(row);
          childrenByParent.set(parent.id, list);
          continue;
        }
        topLevel.push(row);
      }
      const sortPeers = (left: ClosingWorkStage, right: ClosingWorkStage): number => {
        const leftLevel = contractLevel(left);
        const rightLevel = contractLevel(right);
        if (leftLevel !== rightLevel) return leftLevel - rightLevel;
        const byContract = left.contract_number.localeCompare(right.contract_number, 'ru', { numeric: true });
        if (byContract !== 0) return byContract;
        return left.stage_number - right.stage_number;
      };
      topLevel.sort(sortPeers);
      for (const children of childrenByParent.values()) {
        children.sort(sortPeers);
      }
      for (const row of topLevel) {
        const appendWithChildren = (item: ClosingWorkStage): void => {
          result.push(item);
          for (const child of childrenByParent.get(item.id) ?? []) {
            appendWithChildren(child);
          }
        };
        appendWithChildren(row);
      }
    }
    return result;
  }, [filteredMainIds, stagesByMainId, representativeByMainId, sortState, getDeadline]);

  const selectableIds = useMemo(
    () => sortedStages.filter((row) => !row.accounting_closed).map((row) => row.id),
    [sortedStages],
  );
  const allVisibleSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));
  const someVisibleSelected = selectableIds.some((id) => selectedIds.has(id)) && !allVisibleSelected;

  useEffect(() => {
    const knownOpenIds = new Set(
      stages.filter((row) => !row.accounting_closed).map((row) => row.id),
    );
    setSelectedIds((prev) => {
      const next = new Set([...prev].filter((id) => knownOpenIds.has(id)));
      if (next.size === prev.size) return prev;
      return next;
    });
  }, [stages]);

  const handleSort = (key: ClosingStageSortKey): void => {
    setSortState((current) => toggleSort(current, key));
  };

  const toggleSelectAllVisible = (): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        selectableIds.forEach((id) => next.delete(id));
      } else {
        selectableIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const toggleRowSelected = (id: number): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openCloseModal = (): void => {
    if (selectedIds.size === 0) {
      setError('Выберите хотя бы один этап.');
      setSuccess(null);
      return;
    }
    setError('');
    setSuccess(null);
    setClosingDate(todayIsoDate());
    setDateModalOpen(true);
  };

  const handleCloseFromAccounting = async (): Promise<void> => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) {
      setError('Выберите хотя бы один этап.');
      setSuccess(null);
      return;
    }
    if (!closingDate.trim()) {
      setError('Укажите дату закрытия по подписанным актам.');
      return;
    }
    setSaving(true);
    setError('');
    setSuccess(null);
    try {
      const created = await closeClosingWorkStages({
        stage_ids: ids,
        closing_date: closingDate,
      });
      setSuccess(`Создано записей о закрытии: ${created}`);
      setSelectedIds(new Set());
      setDateModalOpen(false);
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка при закрытии этапов');
    } finally {
      setSaving(false);
    }
  };

  const colgroup = (
    <colgroup>
      {canCloseFromAccounting ? (
        <col className="closing-tasks-page__colgrid closing-tasks-page__colgrid--check" />
      ) : null}
      <col className="contracts-page__colgrid contracts-page__colgrid--contractor" />
      <col className="contracts-page__colgrid contracts-page__colgrid--number" />
      <col className="contracts-page__colgrid contracts-page__colgrid--number" />
      <col className="contracts-page__colgrid contracts-page__colgrid--number" />
      <col className="contracts-page__colgrid contracts-page__colgrid--person" />
      <col className="contracts-page__colgrid contracts-page__colgrid--person" />
      <col className="contracts-page__colgrid contracts-page__colgrid--date" />
      <col className="contracts-page__colgrid contracts-page__colgrid--date" />
      <col className="contracts-page__colgrid contracts-page__colgrid--date" />
      <col className="contracts-page__colgrid in-progress-contracts-page__colgrid--overdue" />
      <col className="contracts-page__colgrid closing-contracts-page__colgrid--check" />
    </colgroup>
  );

  return (
    <div className="data-page contracts-page in-progress-contracts-page in-progress-smo-ul-page closing-tasks-page closing-contracts-page">
      {!canOpenPage ? (
        <div className="empty-state">Нет доступа к странице (требуется CONTRACTS_CLOSING CREATE).</div>
      ) : (
        <>
          <div className="page-header-row">
            <h1>Это этапы подлежащие закрытию от Бухгалтерии</h1>
            <div className="page-header-row__actions">
              <button
                type="button"
                className={`filters-toggle ${!filtersOpen && hasActiveFilters ? 'filters-toggle--show-active-dot' : ''}`}
                onClick={() => setFiltersOpen((open) => !open)}
                aria-expanded={filtersOpen}
                aria-label={filtersOpen ? 'Скрыть фильтры' : 'Показать фильтры'}
              >
                Фильтры
                <span className="filters-toggle-badge" aria-hidden />
              </button>
            </div>
            <div className="page-header-row__search">
              <input
                type="text"
                className="search-input"
                placeholder="Поиск по текстовым полям"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
          </div>

          {error && !dateModalOpen ? <div className="error-message">{error}</div> : null}
          {success ? <div className="success-message">{success}</div> : null}

          <div className={`table-layout ${filtersOpen ? '' : 'table-layout--filters-collapsed'}`}>
            {filtersOpen && (
              <aside className="filters-panel">
                <div className="filters-header">
                  <h2>Фильтры</h2>
                  <button type="button" className="link-button" onClick={clearFilters}>
                    Сбросить
                  </button>
                </div>
                <div className="filters-grid">
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Контрагент"
                      options={contractorFilterOptions}
                      selected={filterContractorIds}
                      onSelectedChange={setFilterContractorIds}
                      formatOption={getContractorLabel}
                      isOpen={filterMenuOpen === 'contractor'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'contractor' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Номер договора"
                      options={numberFilterOptions}
                      selected={filterContractNumbers}
                      onSelectedChange={setFilterContractNumbers}
                      formatOption={(v) => v}
                      isOpen={filterMenuOpen === 'contract_number'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'contract_number' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Этап"
                      options={stageFilterOptions}
                      selected={filterStages}
                      onSelectedChange={setFilterStages}
                      formatOption={(v) => v}
                      isOpen={filterMenuOpen === 'stage'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'stage' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Менеджер"
                      options={managerFilterOptions}
                      selected={filterManagerIds}
                      onSelectedChange={setFilterManagerIds}
                      formatOption={getManagerLabel}
                      isOpen={filterMenuOpen === 'manager'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'manager' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="РП"
                      options={projectLeadFilterOptions}
                      selected={filterLeadIds}
                      onSelectedChange={setFilterLeadIds}
                      formatOption={getLeadLabel}
                      isOpen={filterMenuOpen === 'project_lead'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'project_lead' : null)}
                    />
                  </div>
                  <label className="filters-sum-block">
                    <span className="filters-sum-block__title">Начало</span>
                    <div className="filters-sum-block__row">
                      <input
                        type="date"
                        className="filters-sum-block__input"
                        value={filterStartFrom}
                        onChange={(e) => setFilterStartFrom(e.target.value)}
                        title="Начало с"
                      />
                      <input
                        type="date"
                        className="filters-sum-block__input"
                        value={filterStartTo}
                        onChange={(e) => setFilterStartTo(e.target.value)}
                        title="Начало по"
                      />
                    </div>
                  </label>
                  <label className="filters-sum-block">
                    <span className="filters-sum-block__title">Срок</span>
                    <div className="filters-sum-block__row">
                      <input
                        type="date"
                        className="filters-sum-block__input"
                        value={filterEndFrom}
                        onChange={(e) => setFilterEndFrom(e.target.value)}
                        title="Срок с"
                      />
                      <input
                        type="date"
                        className="filters-sum-block__input"
                        value={filterEndTo}
                        onChange={(e) => setFilterEndTo(e.target.value)}
                        title="Срок по"
                      />
                    </div>
                  </label>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Просрочка"
                      options={OVERDUE_FILTER_OPTIONS}
                      selected={filterOverdue}
                      onSelectedChange={setFilterOverdue}
                      formatOption={overdueFilterLabel}
                      isOpen={filterMenuOpen === 'overdue'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'overdue' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Закрыто"
                      options={CIRCLE_FILTER_OPTIONS}
                      selected={filterClosed}
                      onSelectedChange={setFilterClosed}
                      formatOption={(v) => closingStatusLabel(v as SmoUlClosingStatus)}
                      isOpen={filterMenuOpen === 'accounting_closed'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'accounting_closed' : null)}
                    />
                  </div>
                </div>
              </aside>
            )}

            <section className="table-content">
              {canCloseFromAccounting ? (
                <div className="closing-tasks-page__toolbar">
                  <button
                    type="button"
                    className="closing-tasks-page__action-button"
                    onClick={openCloseModal}
                    disabled={saving || selectedIds.size === 0}
                  >
                    Закрыть от Бухгалтерии
                  </button>
                </div>
              ) : null}
              {isLoading ? (
                <div className="loading">Загрузка...</div>
              ) : (
                <div className="table-panel">
                  <div className="table-scroll">
                    <table className="data-table contracts-page__table in-progress-contracts-page__table in-progress-smo-ul-page__table">
                      {colgroup}
                      <thead>
                        <tr>
                          {canCloseFromAccounting ? (
                            <th className="closing-tasks-page__th-check">
                              <input
                                type="checkbox"
                                checked={allVisibleSelected}
                                ref={(el) => {
                                  if (el) el.indeterminate = someVisibleSelected;
                                }}
                                onChange={toggleSelectAllVisible}
                                disabled={selectableIds.length === 0}
                                aria-label="Выбрать все"
                                title="Выбрать все"
                              />
                            </th>
                          ) : null}
                          <th
                            className="sortable contracts-page__th-contractor"
                            onClick={() => handleSort('contractor')}
                            role="button"
                          >
                            Контрагент
                          </th>
                          <th
                            className="sortable contracts-page__th-number"
                            onClick={() => handleSort('contract_number')}
                            role="button"
                          >
                            Номер договора
                          </th>
                          <th
                            className="sortable contracts-page__th-number"
                            onClick={() => handleSort('stage')}
                            role="button"
                          >
                            Этап
                          </th>
                          <th
                            className="sortable contracts-page__th-number"
                            onClick={() => handleSort('result')}
                            role="button"
                          >
                            Ожидаемый результат
                          </th>
                          <th
                            className="sortable contracts-page__th-person"
                            onClick={() => handleSort('manager')}
                            role="button"
                          >
                            Менеджер
                          </th>
                          <th
                            className="sortable contracts-page__th-person"
                            onClick={() => handleSort('projectLead')}
                            role="button"
                          >
                            РП
                          </th>
                          <th
                            className="sortable contracts-page__th-date"
                            onClick={() => handleSort('start_date')}
                            role="button"
                          >
                            Начало
                          </th>
                          <th
                            className="sortable contracts-page__th-date"
                            onClick={() => handleSort('end_date')}
                            role="button"
                          >
                            Срок
                          </th>
                          <th
                            className="sortable contracts-page__th-date"
                            onClick={() => handleSort('accounting_closing_date')}
                            role="button"
                          >
                            Дата закрытия по Бухгалтерии
                          </th>
                          <th
                            className="sortable in-progress-contracts-page__th-overdue"
                            onClick={() => handleSort('overdue')}
                            role="button"
                          >
                            Просрочка
                          </th>
                          <th
                            className="sortable in-progress-smo-ul-page__th-closing closing-contracts-page__th-check"
                            onClick={() => handleSort('accounting_closed')}
                            role="button"
                          >
                            Закрыто
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedStages.length === 0 ? (
                          <tr>
                            <td colSpan={tableColSpan} className="data-table__empty">
                              Ничего не найдено
                            </td>
                          </tr>
                        ) : (
                          sortedStages.map((row, index) => {
                            const isExtension = isDeadlineExtension(row);
                            const deadline = getDeadline(row);
                            const prev = index > 0 ? sortedStages[index - 1] : null;
                            const isNewFamily =
                              !prev || (prev.main_contract_id || prev.contract_id) !== (row.main_contract_id || row.contract_id);
                            const status = stageClosingStatus(row);
                            return (
                              <tr
                                key={row.id}
                                className={`clickable-row ${isNewFamily ? 'contracts-page__row--new-main' : ''} ${isExtension ? 'contracts-page__row--supplementary' : 'contracts-page__row--main'}`}
                                onClick={(e) => {
                                  if (e.ctrlKey || e.metaKey) {
                                    return;
                                  }
                                  navigate(contractDetailPath(row));
                                }}
                              >
                                {canCloseFromAccounting ? (
                                  <td
                                    className="closing-tasks-page__td-check"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {row.accounting_closed ? null : (
                                      <input
                                        type="checkbox"
                                        checked={selectedIds.has(row.id)}
                                        onChange={() => toggleRowSelected(row.id)}
                                        aria-label={`Выбрать этап ${stageLabel(row)}`}
                                      />
                                    )}
                                  </td>
                                ) : null}
                                <td className="contracts-page__td-contractor">
                                  {isExtension ? (
                                    <span
                                      className="contracts-page__tree-mark"
                                      title={`Продление этапа договора ${row.main_contract_number}`}
                                      aria-label={`Продление этапа договора ${row.main_contract_number}`}
                                    >
                                      └──
                                    </span>
                                  ) : (
                                    <div className="contracts-page__clamp-2">{row.contractor_name}</div>
                                  )}
                                </td>
                                <td className="contracts-page__td-number">
                                  <div className="contracts-page__clamp-2">
                                    <strong>{row.contract_number}</strong>
                                  </div>
                                </td>
                                <td className="contracts-page__td-number">
                                  <div className="contracts-page__clamp-2">{stageLabel(row)}</div>
                                </td>
                                <td className="contracts-page__td-number">
                                  <div className="contracts-page__clamp-2">{row.result?.trim() || '—'}</div>
                                </td>
                                <td className="contracts-page__td-person">
                                  <div className="contracts-page__clamp-2">
                                    {isExtension ? '—' : row.manager_name}
                                  </div>
                                </td>
                                <td className="contracts-page__td-person">
                                  <div className="contracts-page__clamp-2">
                                    {isExtension ? '—' : row.project_lead_name}
                                  </div>
                                </td>
                                <td className="contracts-page__td-date">
                                  {row.start_date ? formatDateShort(row.start_date) : '—'}
                                </td>
                                <td className="contracts-page__td-date">
                                  {row.end_date ? formatDateShort(row.end_date) : '—'}
                                </td>
                                <td className="contracts-page__td-date">
                                  {row.accounting_closing_date ? formatDateShort(row.accounting_closing_date) : '—'}
                                </td>
                                <td
                                  className={`in-progress-contracts-page__td-overdue${deadline?.overdue ? ' in-progress-contracts-page__cell--overdue' : ''}`}
                                >
                                  {deadline?.label ? (
                                    <div className="contracts-page__clamp-2 in-progress-contracts-page__deadline">
                                      <span className="in-progress-contracts-page__deadline-label">
                                        {deadline.label.toLowerCase()}
                                      </span>
                                      <span>{formatDeadlineCompact(deadline)}</span>
                                    </div>
                                  ) : (
                                    '—'
                                  )}
                                </td>
                                <td className="in-progress-smo-ul-page__td-closing closing-contracts-page__td-check">
                                  <ClosingStatusIcon
                                    status={status}
                                    closingDate={row.accounting_closing_date}
                                  />
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="table-panel__footer">
                    <table className="data-table data-table--footer contracts-page__table in-progress-contracts-page__table in-progress-smo-ul-page__table">
                      {colgroup}
                      <tfoot>
                        <tr>
                          {canCloseFromAccounting ? <td className="closing-tasks-page__td-check" /> : null}
                          <td>Итого: {sortedStages.length}</td>
                          <td />
                          <td />
                          <td />
                          <td />
                          <td />
                          <td />
                          <td />
                          <td />
                          <td />
                          <td className="in-progress-smo-ul-page__td-closing" />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </section>
          </div>

          {dateModalOpen ? (
            <div
              className="taxes-page__modal-overlay"
              role="presentation"
              onClick={() => {
                if (!saving) setDateModalOpen(false);
              }}
            >
              <div
                className="taxes-page__modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="closing-stage-date-title"
                onClick={(e) => e.stopPropagation()}
              >
                <h2 id="closing-stage-date-title">Дата закрытия по подписанным актам</h2>
                <form
                  className="taxes-page__form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void handleCloseFromAccounting();
                  }}
                >
                  <label className="taxes-page__field">
                    <span>Дата закрытия *</span>
                    <input
                      type="date"
                      value={closingDate}
                      onChange={(e) => setClosingDate(e.target.value)}
                      disabled={saving}
                      required
                    />
                  </label>
                  {error ? <div className="error-message">{error}</div> : null}
                  <div className="taxes-page__modal-actions">
                    <button
                      type="button"
                      className="secondary-button"
                      onClick={() => setDateModalOpen(false)}
                      disabled={saving}
                    >
                      Отмена
                    </button>
                    <button type="submit" className="create-button" disabled={saving || !closingDate}>
                      {saving ? 'Закрытие…' : 'Закрыть'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

export default ClosingStagesPage;
